// src/components/Shared/ResetPasswordPage.jsx
// Landing screen for the emailed link: /reset-password?token=...&email=...
// Sets a new password, then sends the user to sign in with it.

import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { IconEye, IconEyeOff } from './FigmaIcons';
import BackButton from './BackButton';

// Mirrors server/utils/passwordPolicy.js: >=5 chars, one uppercase, one digit.
const isStrongPassword = (value) =>
	typeof value === 'string' && value.length >= 5 && /[A-Z]/.test(value) && /\d/.test(value);

const ResetPasswordPage = () => {
	const [params] = useSearchParams();
	const navigate = useNavigate();
	const token = params.get('token') || '';
	const email = params.get('email') || '';

	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [errorMsg, setErrorMsg] = useState('');
	const [loading, setLoading] = useState(false);
	const [done, setDone] = useState(false);

	const linkLooksValid = Boolean(token && email);

	const handleSubmit = async (e) => {
		e.preventDefault();
		const fields = e.currentTarget.elements;
		const domValue = (n, fallback) => {
			const el = fields.namedItem(n);
			return typeof el?.value === 'string' ? el.value : fallback;
		};
		const pass = domValue('password', password);
		const confirm = domValue('confirmPassword', confirmPassword);
		if (pass !== password) setPassword(pass);
		if (confirm !== confirmPassword) setConfirmPassword(confirm);

		setErrorMsg('');
		if (!pass || !confirm) {
			setErrorMsg('Please enter all fields.');
			return;
		}
		if (pass !== confirm) {
			setErrorMsg('Passwords do not match.');
			return;
		}
		if (!isStrongPassword(pass)) {
			setErrorMsg('Password must be at least 5 characters with one uppercase letter and one number.');
			return;
		}

		setLoading(true);
		try {
			await api.post('/auth/reset-password', { email, token, password: pass });
			setDone(true);
			setTimeout(() => navigate('/login', { replace: true }), 2200);
		} catch (error) {
			setErrorMsg(error.response?.data?.msg || 'Could not reset your password. Try again.');
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="ev-page ev-auth-page">
			<div className="ev-auth-card">
				<div className="ev-back-row">
					<BackButton to="/login" label="Back to sign in" />
				</div>
				<div className="ev-auth-head">
					<div className="ev-auth-logo">
						<img src="/images/eventory-logo.svg" alt="Eventory" />
					</div>
					<h1 className="ev-auth-title">Set a new password</h1>
					{email && !done && <p className="ev-auth-subtitle">For {email}</p>}
				</div>

				{errorMsg && <div className="ev-alert ev-alert-danger">{errorMsg}</div>}

				{!linkLooksValid ? (
					<>
						<div className="ev-alert ev-alert-danger">
							That link is incomplete. Request a new one.
						</div>
						<p className="ev-auth-switch">
							<Link to="/forgot-password" className="ev-inline-link">Request a reset link</Link>
						</p>
					</>
				) : done ? (
					<>
						<div className="ev-alert">
							Password updated. Taking you to sign in…
						</div>
						<p className="ev-auth-switch">
							<Link to="/login" className="ev-inline-link">Sign in now</Link>
						</p>
					</>
				) : (
					<form onSubmit={handleSubmit} className="ev-auth-grid">
						{/* Hidden username field: password managers need it to know
						    which account the new password belongs to. */}
						<input
							type="hidden"
							name="username"
							autoComplete="username"
							value={email}
							readOnly
						/>

						<div className="ev-auth-field">
							<label htmlFor="password">New password</label>
							<div className="ev-auth-input-wrap">
								<input
									type={showPassword ? 'text' : 'password'}
									id="password"
									name="password"
									autoComplete="new-password"
									className="ev-auth-input"
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									placeholder="Strong password"
									required
								/>
								<button
									type="button"
									className="ev-auth-eye"
									onClick={() => setShowPassword((prev) => !prev)}
									aria-label={showPassword ? 'Hide password' : 'Show password'}
								>
									{showPassword ? <IconEyeOff className="ev-icon-svg" /> : <IconEye className="ev-icon-svg" />}
								</button>
							</div>
							<small className="ev-subtle">
								At least 5 characters, with one uppercase letter and one number.
							</small>
						</div>

						<div className="ev-auth-field">
							<label htmlFor="confirmPassword">Confirm new password</label>
							<div className="ev-auth-input-wrap">
								<input
									type={showPassword ? 'text' : 'password'}
									id="confirmPassword"
									name="confirmPassword"
									autoComplete="new-password"
									className="ev-auth-input"
									value={confirmPassword}
									onChange={(e) => setConfirmPassword(e.target.value)}
									placeholder="Repeat the password"
									required
								/>
							</div>
						</div>

						<button type="submit" className="ev-auth-primary" disabled={loading}>
							{loading ? 'Updating…' : 'Update password'}
						</button>
					</form>
				)}
			</div>
		</div>
	);
};

export default ResetPasswordPage;
